/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable camelcase */
import React, {
    useState,
    useEffect,
    useMemo,
    Suspense,
    lazy,
    useRef,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useKeyboard, useFocusWithin } from '@react-aria/interactions';
import { DismissButton } from '@react-aria/overlays';

import { AutocompleteService } from '../data/AutocompleteService';
import { useStore, StoreProvider } from './store';
import { initialState, searchReducer } from './reducer';
import { useArrayRef, getParam, getTracker } from './helpers';

/**
 * Lazy load result templates. We could not use standard hook to load, as we need to pass array of refs.
 * With memo we caching function but still have ability to rebuild props when refs are changed.
 *
 * @param {object} props Object with props we want to pass into component.
 *
 * @returns {JSX.Element}
 * @constructor
 */
const LoadTemplates = (props) => {
    const { template } = props;
    const componentName = template.toLowerCase();
    const DynamicComponent = useMemo(
        () => lazy(() => import(`./templates/${componentName}`)),
        []
    );

    return (
        <Suspense
            key={`component-${componentName}`}
            fallback={<span className="sr-only">Loading template...</span>}
        >
            <DynamicComponent {...props} />
        </Suspense>
    );
};

/**
 * Loading indicator.
 *
 * @param {bool} fetching Flag indicating loading state.
 * @param {function} onSubmit Callback function to execute when submit event occur.
 * @param {string} componentId Id of the component.
 *
 * @return {JSX.Element}
 * @constructor
 */
const Loader = ({ fetching, onSubmit, componentId }) => {
    if (fetching) {
        return (
            <div className="autocomplete-concierge__loader" aria-hidden="true">
                <svg className="svg-icon" role="img" focusable={false}>
                    <use href="#spinner" />
                </svg>
            </div>
        );
    }
    return (
        <button
            type="submit"
            className="autocomplete-concierge__submit"
            onClick={onSubmit}
            aria-labelledby={`${componentId}-submit-label`}
        >
            <svg className="svg-icon" role="img" focusable={false}>
                <title id={`${componentId}-submit-label`}>Submit search</title>
                <use href="#search" />
            </svg>
        </button>
    );
};

function Autocomplete(props) {
    const {
        id,
        templates,
        placeholder,
        debounce,
        action,
        method,
        hiddenFields,
        inputAttributes,
        showSubmit,
        onClose,
        showClear,
        showLoader,
    } = props;

    const { setSelectedIndex, selectedIndex, count, fetchResults, fetching } =
        useStore();

    const [activeDescendant, setActiveDescendant] = useState(null);
    const [submit, setSubmit] = useState(null);
    const [showResults, setShowResults] = useState(null);
    const [query, setQuery] = useState(``);
    const [readerAnnounce, setReaderAnnounce] = useState(``);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [gridClasses, setGridClasses] = useState(
        `autocomplete-concierge__results`
    );

    const formRef = useRef();
    const sliderRef = useRef();
    const searchBox = useRef(null);
    const tracker = getTracker();

    const [elements, childRefs] = useArrayRef();

    const { keyboardProps } = useKeyboard({
        onKeyUp: (e) => {
            let index = -1;
            // ESC
            if (e.keyCode === 27) {
                // Hide results
                setIsInputFocused(false);
                // Reset selected item
                setSelectedIndex(-1);

                // If second time enter hit we want to call onClose cllaback function, in this example
                // we will close popover we have autocomplete nested.
                if (!showResults) {
                    onClose();
                }
                // After closing results regain input field focus
                if (searchBox.current) {
                    searchBox.current.focus();
                }
                return;
            }
            // Down
            if (e.keyCode === 40) {
                index = selectedIndex + 1 >= count ? 0 : selectedIndex + 1;
                // When more items than view can accommodate, we want scroll to follow the button down.
                if (elements[index]) {
                    elements[index].scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                        inline: 'start',
                    });
                }
            }
            // Up
            if (e.keyCode === 38) {
                index = selectedIndex - 1 < 0 ? count - 1 : selectedIndex - 1;
                // When more items than view can accommodate, we want scroll to follow the button up.
                if (elements[index]) {
                    elements[index].scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                        inline: 'start',
                    });
                }
            }
            // Enter
            if (e.keyCode === 13) {
                // Nothing selected and enter hit we will post form.
                if (selectedIndex === -1) {
                    setSubmit(true);
                    return;
                }

                // We will fire onClick event when enter hit
                elements[selectedIndex].click();
            }

            setSelectedIndex(index);
            setActiveDescendant(index);
            setIsInputFocused(true);
        },
        onKeyDown: (e) => {
            // This wil prevent moving cursor on input field when press down or up also stop form from being posted when enter pressed.
            if (
                e.keyCode === 40 ||
                e.keyCode === 38 ||
                e.keyCode === 13 ||
                e.keyCode === 27
            ) {
                e.preventDefault();
            } else {
                // Regain focus on input field when other buttons are entered.
                searchBox.current.focus();
            }
            return false;
        },
    });

    // This is to handle clicks outside the combobox.
    const { focusWithinProps } = useFocusWithin({
        onFocusWithin: () => {
            setIsInputFocused(true);
        },
        onBlurWithin: () => {
            setIsInputFocused(false);
        },
    });

    // When count or focus change we will hide or show result.
    useEffect(() => {
        setShowResults(() => isInputFocused && count > 0);
    }, [count, isInputFocused]);

    // Animation handler.
    useEffect(() => {
        const classes = ['autocomplete-concierge__results'];

        if (fetching && showResults) {
            classes.push('autocomplete-concierge__results--expanding');
            setGridClasses(classes.join(' '));
        }

        if (!fetching && showResults) {
            classes.push('autocomplete-concierge__results--open');
            setGridClasses(classes.join(' '));
        }

        if (!fetching && !showResults) {
            classes.push('autocomplete-concierge__results--collapsing');
            setGridClasses(classes.join(' '));
        }
    }, [showResults]);

    // On page load update input field with query string.
    useEffect(() => {
        const queryParam = getParam(document.location.href, 'query');
        setQuery(queryParam || ``);
    }, []);

    // Let the screen readers know we have or not results.
    useEffect(() => {
        if (!query) {
            return;
        }
        const message =
            count > 0
                ? `${count} results for ${query}`
                : `No results found for ${query}`;

        setReaderAnnounce(message);
    }, [count, query]);

    // When query changed we debounce network request.
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            // When reset query, we will reestablish focus on input field.
            if (query === ``) {
                searchBox.current.focus();
            }
            await fetchResults(query, templates);
        }, debounce);

        return () => clearTimeout(delayDebounceFn);
    }, [query]);

    // This is to handle form submissions including tracker.
    useEffect(() => {
        if (submit && formRef.current && searchBox.current) {
            searchBox.current.value = query;
            if (tracker) {
                const trackingEvent = {
                    TYPE: 'SUBMIT',
                    title: 'NEEDS_REFRESH',
                    url: window.location.href,
                    time: Date.now(),
                };
                tracker.trackedEventWithCb(
                    trackingEvent,
                    formRef.current.submit()
                );
                return;
            }

            formRef.current.submit();
        }
    }, [submit]);

    const handleInputOnChange = ({ currentTarget: { value } }) => {
        setQuery(value);
    };

    const trackedClick = (url, title, cb) => {
        if (tracker) {
            const trackingEvent = {
                TYPE: 'CLICK',
                url,
                query,
                title,
            };
            tracker.trackedEventWithCb(trackingEvent, cb);
        }
        if (cb && !tracker) {
            cb();
        }
    };

    // Standard onClick handler, can be overridden within template component if needed.
    const handleClick = useCallback((e, data) => {
        // Hijack's enter button and touch and click events.
        e.preventDefault();

        // eslint-disable-next-line no-shadow
        const { action, action_t, title } = data;

        if (action_t === 'Q' || action_t === undefined) {
            setQuery(action || data);
            setSubmit(true);
            return;
        }

        trackedClick(
            action,
            title,
            // eslint-disable-next-line no-return-assign
            () => (document.location.href = action)
        );
    }, []);

    return (
        <div
            className="autocomplete-concierge no-wysiwyg"
            role="search"
            {...focusWithinProps}
        >
            <form
                ref={formRef}
                action={action}
                method={method}
                className="autocomplete-concierge__form"
            >
                <label
                    className="sr-only"
                    aria-live="polite"
                    id={`${id}-label`}
                >
                    {readerAnnounce}
                </label>
                {showLoader && (
                    <Loader
                        fetching={fetching}
                        onSubmit={() => setSubmit(true)}
                        componentId={id}
                    />
                )}
                <input
                    id={`${id}-inputField`}
                    ref={searchBox}
                    type="text"
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={showResults}
                    aria-haspopup="grid"
                    aria-labelledby={`${id}-label`}
                    aria-autocomplete="list"
                    aria-controls={`${id}-grid`}
                    aria-activedescendant={
                        activeDescendant === -1 ? undefined : activeDescendant
                    }
                    onChange={handleInputOnChange}
                    value={query}
                    placeholder={placeholder}
                    className="autocomplete-concierge__input"
                    name="query"
                    {...keyboardProps}
                    {...inputAttributes}
                />
                {hiddenFields &&
                    hiddenFields.map((el) => (
                        <input
                            key={el.id}
                            type="hidden"
                            name={el.name}
                            value={el.value}
                        />
                    ))}
                <button
                    type="button"
                    className={[
                        `autocomplete-concierge__submit`,
                        !showClear || (showClear && query.length === 0)
                            ? `hidden`
                            : ``,
                    ].join(' ')}
                    onClick={() => setQuery(``)}
                    onKeyUp={(e) => {
                        e.preventDefault();
                        if (e.keyCode === 13) {
                            setQuery(``);
                        }
                    }}
                >
                    <svg className="svg-icon" role="img" focusable={false}>
                        <title>Clear search</title>
                        <use href="#close" />
                    </svg>
                    Clear
                </button>
                {showSubmit && !showLoader && (
                    <button
                        type="submit"
                        className="autocomplete-concierge__submit"
                        onClick={() => setSubmit(true)}
                        aria-labelledby={`${id}-submit-label`}
                    >
                        <svg className="svg-icon" role="img" focusable={false}>
                            <title id={`${id}-submit-label`}>
                                Submit search
                            </title>
                            <use href="#search" />
                        </svg>
                    </button>
                )}
            </form>
            <div
                ref={sliderRef}
                role="grid"
                aria-labelledby={`${id}-label`}
                aria-hidden={!showResults}
                id={`${id}-grid`}
                className={gridClasses}
                tabIndex={-1}
                {...keyboardProps}
                style={{ display: !showResults ? 'none' : '' }}
            >
                {templates &&
                    templates.map((el, templateId) =>
                        LoadTemplates({
                            ...el,
                            templateId,
                            handleClick,
                            trackedClick,
                            childRefs,
                        })
                    )}
            </div>
            <DismissButton onDismiss={() => setShowResults(false)} />
        </div>
    );
}

export default function AutocompleteProvider(props) {
    const { adapters } = props;
    const autocompleteService = new AutocompleteService(adapters);

    return (
        <StoreProvider
            initialState={initialState}
            reducer={searchReducer}
            autocompleteService={autocompleteService}
        >
            <Autocomplete {...props} />
        </StoreProvider>
    );
}

const { string, arrayOf, shape, number, bool, func } = PropTypes;

Autocomplete.propTypes = {
    id: string,
    templates: arrayOf(
        shape({
            id: string,
            label: string,
            template: string,
            serviceUrl: string,
            params: string,
        })
    ).isRequired,
    placeholder: string,
    debounce: number,
    action: string,
    method: string,
    showSubmit: bool,
    isOpen: bool,
    onClose: func,
    showClear: bool,
    showLoader: bool,
};

Autocomplete.defaultProps = {
    id: 'autocomplete-search',
    placeholder: 'Start your search here...',
    debounce: 500,
    action: 'search.html',
    method: 'GET',
    showSubmit: true,
    isOpen: false,
    onClose: () => {},
    showClear: false,
    showLoader: false,
};
